import { useCallback, useEffect, useRef, useState } from "react";
import {
  ApiError,
  compareXeroStatement,
  getXeroShortcode,
  listXeroStatements,
  saveBlob,
  saveXeroStatement,
} from "../lib/api";
import type { Client, XeroComparison, XeroFigures, XeroStatement as Saved } from "../lib/api";
import { describeError } from "../lib/errors";
import { formatWhen } from "../lib/format";
import { base64Of, blobOf, extensionVersion, fetchStatement, fileKind } from "../lib/extension";

/** The boxes shown, in the order the Activity Statement lists them. */
const BOXES: [string, string][] = [
  ["G1", "Total sales"],
  ["G2", "Export sales"],
  ["G3", "Other GST-free sales"],
  ["G10", "Capital purchases"],
  ["G11", "Non-capital purchases"],
  ["1A", "GST on sales"],
  ["1B", "GST on purchases"],
  ["W1", "Total salary and wages"],
  ["W2", "Amounts withheld from W1"],
  ["4", "PAYG tax withheld"],
  ["5A", "PAYG instalment"],
  ["5B", "PAYG instalment credit"],
  ["8A", "Owed to the ATO"],
  ["8B", "Owed by the ATO"],
  ["9", "Payment or refund"],
];

const money = (v: unknown) =>
  typeof v === "number"
    ? v.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : "—";

/** This API's own messages say what happened for these routes; prefer them. */
function explain(exc: unknown, fallback: string): string {
  if (exc instanceof ApiError && exc.message && !exc.message.startsWith("Request failed")) return exc.message;
  const { message, nextStep } = describeError(exc, fallback);
  return nextStep ? `${message} ${nextStep}` : message;
}

function FiguresTable({ figures }: { figures: XeroFigures }) {
  const rows = BOXES.filter(([box]) => typeof figures[box] === "number");
  return (
    <table className="xero-figures">
      <thead>
        <tr>
          <th scope="col">Box</th>
          <th scope="col">Label</th>
          <th scope="col" className="num">Xero</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(([box, label]) => (
          <tr key={box}>
            <th scope="row">{box}</th>
            <td>{label}</td>
            <td className="num">{money(figures[box])}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

type Period = { start: string; end: string };

export type XeroFetchStatus =
  | { kind: "checking" }
  | { kind: "missing" }
  | { kind: "idle" }
  | { kind: "fetching"; period: Period }
  | { kind: "saved"; period: Period }
  | { kind: "failed"; period: Period; message: string };

export interface XeroFetcher {
  status: XeroFetchStatus;
  /** Bumped after each save, so saved lists know to read again. */
  savedVersion: number;
  /** Why the last save went without Xero's Excel file, if it did. */
  fileNote: string | null;
  /** The Excel file from the last fetch, while this page is open. */
  lastFile: { period: Period; dataUrl: string; filename: string | null } | null;
  /** Fetch Xero's statement for the period and save it to BAS. Never throws. */
  run: (period: Period) => Promise<void>;
}

const isPeriod = (p: Period) =>
  /^\d{4}-\d{2}-\d{2}$/.test(p.start) && /^\d{4}-\d{2}-\d{2}$/.test(p.end) && p.start <= p.end;

/**
 * Fetch Xero's statement through the Casa extension and save it, in one go.
 *
 * Lives in the Generate panel rather than in the section that shows it, so a
 * fetch started by Generate carries on when the panel switches to the running
 * job.
 */
export function useXeroFetch(client: Client, token: string | undefined, userName = ""): XeroFetcher {
  const [status, setStatus] = useState<XeroFetchStatus>({ kind: "checking" });
  const [savedVersion, setSavedVersion] = useState(0);
  const [lastFile, setLastFile] = useState<XeroFetcher["lastFile"]>(null);
  const [fileNote, setFileNote] = useState<string | null>(null);
  const busy = useRef(false);

  useEffect(() => {
    let live = true;
    void extensionVersion().then((v) => {
      if (live) setStatus(v === null ? { kind: "missing" } : { kind: "idle" });
    });
    return () => {
      live = false;
    };
  }, []);

  const run = useCallback(async (period: Period) => {
    if (!token || busy.current || !isPeriod(period)) return;
    busy.current = true;
    try {
      if ((await extensionVersion()) === null) {
        setStatus({ kind: "missing" });
        return;
      }
      setStatus({ kind: "fetching", period });
      const { short_code } = await getXeroShortcode(token, client.client_id);
      const result = await fetchStatement({ shortcode: short_code, name: client.name, period });
      if (!result.ok) {
        setStatus({
          kind: "failed",
          period,
          message: result.signedOut
            ? "You're not signed in to Xero in this browser. Sign in at go.xero.com, then try again."
            : result.message,
        });
        return;
      }
      const statement = result.statement;
      // The figures are saved whatever happens to the file; the file is
      // attached only when Xero's export really was a workbook.
      let missingFile = statement.fileError;
      let fileBase64 = "";
      if (statement.file) {
        setLastFile({ period, dataUrl: statement.file.dataUrl, filename: statement.file.filename });
        const file = fileKind(statement.file.dataUrl);
        if (file.kind.startsWith("an Excel workbook")) fileBase64 = base64Of(statement.file.dataUrl);
        else missingFile = `Xero's export came back as ${file.kind}, not an Excel workbook.`;
      }
      await saveXeroStatement(token, client.client_id, {
        period_start: period.start,
        period_end: period.end,
        short_code,
        figures: statement.figures,
        file_base64: fileBase64,
        fetched_by_name: userName,
      });
      setFileNote(missingFile ? `Figures saved without Xero's Excel file. ${missingFile}` : null);
      setSavedVersion((v) => v + 1);
      setStatus({ kind: "saved", period });
    } catch (exc) {
      setStatus({ kind: "failed", period, message: explain(exc, "Could not fetch Xero's statement.") });
    } finally {
      busy.current = false;
    }
  }, [token, client.client_id, client.name, userName]);

  return { status, savedVersion, lastFile, fileNote, run };
}

const samePeriod = (a: Period, b: Period) => a.start === b.start && a.end === b.end;

const money2 = (v: string | null) =>
  v === null ? "—" : Number(v).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** A stable key per line; identical lines get a running number. */
function keyed(lines: XeroComparison["only_in_xero"]) {
  const seen = new Map<string, number>();
  return lines.map((line) => {
    const base = [line.date, line.details, line.account, line.gross, line.gst].join("|");
    const n = (seen.get(base) ?? 0) + 1;
    seen.set(base, n);
    return { key: `${base}#${n}`, line };
  });
}

function LineList({ title, lines }: { title: string; lines: XeroComparison["only_in_xero"] }) {
  if (lines.length === 0) return null;
  return (
    <details className="xero-lines" open>
      <summary>
        {title} ({lines.length})
      </summary>
      <table className="xero-figures">
        <thead>
          <tr>
            <th scope="col">Date</th>
            <th scope="col">Details</th>
            <th scope="col">Account</th>
            <th scope="col" className="num">Gross</th>
            <th scope="col" className="num">GST</th>
          </tr>
        </thead>
        <tbody>
          {keyed(lines).map(({ key, line: l }) => (
            <tr key={key}>
              <td>{l.date}</td>
              <td>{l.details}</td>
              <td>{l.account}</td>
              <td className="num">{money2(l.gross)}</td>
              <td className="num">{money2(l.gst)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </details>
  );
}

/** Xero's statement beside BAS's run for the same period. */
function Comparison({ result }: { result: XeroComparison }) {
  const differing = result.boxes.filter((b) => b.difference !== null && b.difference !== "0.00");
  const explained = differing.every((b) => b.explained_by_lines === b.difference);
  return (
    <div className="xero-compare">
      <h5 className="label">Compared with BAS's workpaper</h5>
      {differing.length === 0 ? (
        <p className="ok-line">Every box matches Xero{result.boxes.some((b) => b.xero !== null && b.xero !== b.xero_exact) ? " (Xero's statement drops the cents)" : ""}.</p>
      ) : (
        <p className="diff-line">
          BAS differs from Xero on {differing.map((b) => b.box).join(", ")}.
          {explained && (result.only_in_xero.length > 0 || result.only_in_bas.length > 0)
            ? " The lines below account for all of it."
            : " Not all of it is explained by individual lines; check the figures in Xero."}
        </p>
      )}
      <table className="xero-figures">
        <thead>
          <tr>
            <th scope="col">Box</th>
            <th scope="col" className="num">Xero</th>
            <th scope="col" className="num">BAS</th>
            <th scope="col" className="num">Difference</th>
          </tr>
        </thead>
        <tbody>
          {result.boxes.map((b) => (
            <tr key={b.box} className={b.difference && b.difference !== "0.00" ? "differs" : undefined}>
              <th scope="row">{b.box}</th>
              <td className="num">{money2(b.xero_exact ?? b.xero)}</td>
              <td className="num">{money2(b.bas)}</td>
              <td className="num">{b.difference === null ? "—" : b.difference === "0.00" ? "—" : money2(b.difference)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <LineList title="In Xero, not in BAS" lines={result.only_in_xero} />
      <LineList title="In BAS, not in Xero" lines={result.only_in_bas} />
      {result.only_in_xero.some((l) => /expense claim/i.test(l.details)) && (
        <p className="muted small">
          Expense claims are missing because Xero does not let BAS read them; Xero's figure includes them.
        </p>
      )}
      {!result.line_match_exact && (
        <p className="muted small">
          Cash basis: lines are matched on their document dates, so some may show as differences that are only
          timing. The box figures are exact.
        </p>
      )}
    </div>
  );
}


/**
 * Xero's own Activity Statement for the period: what BAS has saved, and the
 * state of any fetch. Generate fetches it by itself; the button is for again.
 */
export function XeroStatement({
  client,
  period,
  token,
  fetcher,
  runKey = "",
}: {
  client: Client;
  period: Period;
  token: string | undefined;
  fetcher: XeroFetcher;
  /** Changes when a run of this period finishes, so the comparison is redone. */
  runKey?: string;
}) {
  const [saved, setSaved] = useState<Saved[] | null>(null);
  const valid = isPeriod(period);
  const { status, savedVersion, lastFile, fileNote, run } = fetcher;

  useEffect(() => {
    if (!token || !valid) return;
    let live = true;
    listXeroStatements(token, client.client_id, period.end)
      .then((all) => {
        if (live) setSaved(all.filter((s) => s.period_start === period.start));
      })
      .catch(() => {
        if (live) setSaved(null);
      });
    return () => {
      live = false;
    };
    // savedVersion is read only to re-run this after a save.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, client.client_id, period.start, period.end, valid, savedVersion]);

  const latest = saved && saved.length > 0 ? saved[0] : null;
  const [comparison, setComparison] = useState<{ id: string; result: XeroComparison | null; note: string | null } | null>(null);
  const latestId = latest && latest.has_file !== false ? latest.statement_id : null;

  useEffect(() => {
    if (!token || !latestId) return;
    let live = true;
    compareXeroStatement(token, client.client_id, latestId)
      .then((result) => {
        if (live) setComparison({ id: latestId, result, note: null });
      })
      .catch((exc) => {
        // No finished run yet is normal while one is generating; say so quietly.
        if (live) setComparison({ id: latestId, result: null, note: exc instanceof ApiError ? exc.message : null });
      });
    return () => {
      live = false;
    };
  }, [token, client.client_id, latestId, runKey]);

  const forThis = "period" in status && samePeriod(status.period, period);
  const fetching = forThis && status.kind === "fetching";
  const file = lastFile && samePeriod(lastFile.period, period) ? lastFile : null;

  return (
    <section className="xero-statement" aria-labelledby={`xs-${client.client_id}`}>
      <h4 id={`xs-${client.client_id}`} className="label">Xero's Activity Statement</h4>

      {status.kind === "missing" && (
        <p className="muted">
          Install the Casa extension from the <strong>Tools</strong> tab and BAS will fetch Xero's
          own statement, including unfiled amounts, every time you generate a workpaper.
        </p>
      )}

      {fetching && (
        <p role="status">Fetching Xero's statement… a Xero tab opens in the background and closes itself.</p>
      )}

      {forThis && status.kind === "failed" && (
        <div className="field-error" role="alert">{status.message}</div>
      )}

      {forThis && status.kind === "saved" && fileNote && <p className="muted small">{fileNote}</p>}

      {latest && (
        <div className="notice success">
          <p>
            Saved · fetched {formatWhen(latest.fetched_at)}
            {latest.fetched_by_name ? ` by ${latest.fetched_by_name}` : ""}
            {latest.figures.includesUnfiledAmounts === true ? " · includes unfiled amounts" : ""}
            {latest.has_file === false ? " · figures only" : ""}
          </p>
          <FiguresTable figures={latest.figures} />
        </div>
      )}

      {comparison && comparison.id === latestId && comparison.result && <Comparison result={comparison.result} />}
      {comparison && comparison.id === latestId && !comparison.result && comparison.note && (
        <p className="muted small">{comparison.note}</p>
      )}

      {status.kind !== "missing" && status.kind !== "checking" && !fetching && (
        <div className="row">
          {status.kind === "idle" && !latest ? (
            <span className="muted small">Fetched by itself when you click Generate.</span>
          ) : (
            <button type="button" disabled={!valid || !token} onClick={() => void run(period)}>
              {forThis && status.kind === "failed" ? "Try again" : "Fetch again from Xero"}
            </button>
          )}
          {file && (
            <button
              type="button"
              className="link"
              onClick={() => saveBlob(blobOf(file.dataUrl), file.filename || `${client.name} - Activity Statement ${period.start} to ${period.end}.xlsx`)}
            >
              Download Excel
            </button>
          )}
        </div>
      )}
    </section>
  );
}
